import { useState } from "react";
import {
  Space,
  Table,
  Tag,
  Popconfirm,
  Switch,
  Pagination,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { IoTrashOutline } from "react-icons/io5";
import { BiEdit } from "react-icons/bi";
import { useNavigate } from "react-router-dom";

const TYPE_LABELS: Record<string, string> = {
  free: "Free",
  trial: "Trial",
  monthly: "Monthly",
  quarterly: "Quarterly",
  yearly: "Yearly",
  print_combo: "Print Combo",
  custom: "Custom",
};

function formatDuration(plan: any) {
  const v = Number(plan?.duration_value) || 0;
  const t = plan?.duration_type || "";
  if (!v) return "-";
  const label = v === 1 ? t.replace(/s$/, "") : t;
  return `${v} ${label}`;
}

function formatPrice(plan: any) {
  const price = Number(plan?.price) || 0;
  if (price === 0) return "Free";
  return <span>{price}</span>;
}

function Datatable(props: any) {
  const [sortedInfo, setSortedInfo] = useState<any>({});
  const navigate = useNavigate();

  const handleChange = (_p: any, _f: any, sorter: any) => {
    setSortedInfo(sorter);
  };

  const columns: ColumnsType<any> = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 50,
      render: (_text, _record, index) => <div>{index + 1}</div>,
    },
    {
      title: "Plan Name",
      dataIndex: "plan_name",
      key: "plan_name",
      sorter: (a: any, b: any) =>
        String(a.plan_name || "").localeCompare(String(b.plan_name || "")),
      sortOrder: sortedInfo.columnKey === "plan_name" && sortedInfo.order,
      render: (text, record: any) => (
        <div
          style={{ cursor: "pointer", fontWeight: 500 }}
          onClick={() => navigate(`/Auth/subscription-plans/form/${record.id}`)}
        >
          {text}
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 130,
      render: (text) => (
        <Tag color="blue">{TYPE_LABELS[String(text)] || text}</Tag>
      ),
    },
    {
      title: "Duration",
      key: "duration",
      width: 130,
      render: (_t, record: any) => <div>{formatDuration(record)}</div>,
    },
    {
      title: "Price",
      key: "price",
      width: 150,
      render: (_t, record: any) => <div>{formatPrice(record)}</div>,
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
      width: 100,
      render: (text: string) => (
        <Tag color={text === "gulf" ? "orange" : text === "india" ? "blue" : "default"}>
          {text === "gulf" ? "Gulf" : text === "india" ? "India" : "-"}
        </Tag>
      ),
    },
    {
      title: "Status",
      key: "status",
      width: 120,
      render: (_t, record: any) => (
        <Space size="middle">
          <Tag
            color={record?.status === "active" ? "green" : "red"}
            style={{ fontSize: "10px" }}
          >
            {record?.status === "active" ? "Active" : "Archived"}
          </Tag>
        </Space>
      ),
    },
    {
      title: "Active",
      key: "toggle",
      width: 80,
      render: (_t, record: any) => (
        <Switch
          checked={record?.status === "active"}
          loading={props.togglingId === record?.id}
          onChange={() => props.onToggleStatus(record)}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 110,
      render: (_, record: any) => (
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            alignContent: "center",
          }}
        >
          <Space size="middle" style={{ marginRight: 10 }}>
            <BiEdit
              size={20}
              color="#f5a442"
              cursor={"pointer"}
              onClick={() =>
                navigate(`/Auth/subscription-plans/form/${record.id}`)
              }
            />
          </Space>
          <Popconfirm
            title="Delete Plan"
            description={`Delete "${record.plan_name}"? Archived plans are safer if subscriptions reference this plan.`}
            okText="Yes"
            cancelText="No"
            onConfirm={() => props.onDelete(record.id)}
          >
            <Space size="middle">
              <IoTrashOutline size={20} color="red" cursor={"pointer"} />
            </Space>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div style={{ marginTop: 10 }}>
      <Table
        columns={columns}
        dataSource={props.data}
        pagination={false}
        onChange={handleChange}
        size="small"
        rowKey="id"
      />
      <br />
      <div style={{ float: "right" }}>
        <Pagination
          responsive
          current={Number(props?.page) || 1}
          total={props.meta && props.meta}
          pageSize={Number(props?.pagesize) || 10}
          showSizeChanger
          showTotal={(total) => `Total ${total} plans`}
          onChange={(p, t) => {
            props.onPageChange(p, t);
          }}
        />
      </div>
    </div>
  );
}

export default Datatable;
